import { PropsWithChildren, ReactNode, Children, Fragment, useMemo } from "react";
import MenuFilters from "@/components/ExpandedFilters/MenuFilters";
import ExpandedFiltersWrapper from "@/components/ExpandedFilters/ExpandedFiltersWrapper";
import IconButton from "@/components/IconButton";
import Button from "@/components/Button";
import Chip from "@/components/Chip";
import Checkbox from "@/components/Checkbox";
import Menu, { MenuItem } from "@/components/Menu";
import pluralize from "@/lib/pluralize";
import { TableAction } from "./TableHeading";
import type { BulkSelectionProps } from "@/hooks/useBulkSelection";
import classNames from "classnames";

/**
 * Configuration for a filter chip that can be displayed in TableControls
 * @property label - The text label displayed on the chip
 * @property onClick - Handler called when the chip is clicked
 * @property selected - Whether the chip is currently selected/active (checkmark automatically shown when selected)
 * @property softDisabled - Keeps a selected chip focusable but not togglable (last remaining required filter)
 */
export interface FilterChipConfig {
  label: string;
  onClick?: () => void;
  selected?: boolean;
  softDisabled?: boolean;
}

/**
 * Props for TableControls component
 * @property pagination - Optional pagination element (automatically hidden when isSelectedOnly is true)
 * @property activeFilters - Number of active filters for badge display
 * @property onSortClick - Optional sort button handler for mobile
 * @property useExpandedFilters - Enable ExpandedFiltersWrapper for automatic filter overflow on desktop (like People table)
 * @property expandedFiltersState - Filter state for ExpandedFiltersWrapper (required if useExpandedFilters is true)
 * @property onClearAllFilters - Clear all filters handler (required if useExpandedFilters is true)
 * @property bulkActions - Bulk action buttons shown when rows are selected (replaces filters)
 * @property rowSelectionCount - Number of selected rows (triggers bulk action display, can use bulkSelection instead)
 * @property onShowSelectedOnly - Handler for "Show Selected Only" chip toggle (can use bulkSelection instead)
 * @property isSelectedOnly - Whether "Show Selected Only" is currently active (can use bulkSelection instead)
 * @property bulkSelection - Grouped bulk selection props from useBulkSelection hook (optional convenience)
 * @property onDeselectAll - Handler for "Deselect All" button
 * @property totalRowCount - Number of currently selected rows for "Deselect All X Rows" label
 * @property onToggleAllRows - Handler to select/deselect all rows for auto-generated mobile checkbox. Called with true to select all, false to deselect all.
 * @property allRowsSelected - Whether all visible rows are currently selected (for checkbox checked state)
 * @property someRowsSelected - Whether some (but not all) rows are selected (for checkbox indeterminate state)
 * @property filterChips - Array of filter chip configurations to display below the search input. Chips handle their own filter states and are shown by default (not conditional on row selection). Use chips for quick-access toggling of table state that should always be visible (unlike filters which can become hidden in side sheets on smaller devices).
 * @property filterChipsAriaLabel - Accessible name for the filter chip group
 * @property mutuallyExclusiveChips - When true, only one chip can be selected at a time. Clicking a chip will automatically deselect all other chips.
 * @property onSelectAllAcrossPages - Handler to select all matching rows across all pages. When provided, a "Select all X matching" text button appears in the selection bar after rows are selected.
 * @property selectAllAcrossPagesLoading - Whether the cross-page selection is loading (shows "Selecting..." label on the button)
 * @property totalMatchingCount - Total number of matching rows across all pages (used in the "Select all X matching" button label)
 * @property afterSearch - Control rendered after the first child (usually SearchInput). Stays out of the compact filter sheet: centered below search on compact layouts, to the right of search on desktop. Use for always-visible companions such as a month pager.
 */
export interface TableControlsProps {
  pagination?: ReactNode;
  activeFilters?: number;
  onSortClick?: () => void;
  useExpandedFilters?: boolean;
  expandedFiltersState?: Record<string, boolean>;
  onClearAllFilters?: () => void;
  bulkActions?: TableAction[];
  rowSelectionCount?: number;
  onShowSelectedOnly?: () => void;
  isSelectedOnly?: boolean;
  bulkSelection?: BulkSelectionProps;
  onDeselectAll?: () => void;
  totalRowCount?: number;
  onToggleAllRows?: (checked: boolean) => void;
  allRowsSelected?: boolean;
  someRowsSelected?: boolean;
  filterChips?: FilterChipConfig[];
  filterChipsAriaLabel?: string;
  mutuallyExclusiveChips?: boolean;
  onSelectAllAcrossPages?: () => void;
  selectAllAcrossPagesLoading?: boolean;
  totalMatchingCount?: number;
  afterSearch?: ReactNode;
}

function SearchWithAfterSearch({ search, afterSearch }: { search: ReactNode; afterSearch?: ReactNode }) {
  if (search == null && afterSearch == null) {
    return null;
  }

  if (!afterSearch) {
    return <div className="w-full lg:max-w-[320px]">{search}</div>;
  }

  return (
    <div className="flex flex-col lg:flex-row lg:items-center gap-3 min-w-0 w-full">
      <div className="w-full min-w-0 lg:w-80">{search}</div>
      <div className="flex-shrink-0 self-center lg:self-auto" data-testid="table-controls-after-search">
        {afterSearch}
      </div>
    </div>
  );
}

/**
 * TableControls component provides consistent responsive layout for table filters and search:
 * - Search/filter inputs (via children)
 * - Optional pagination (shown inline on desktop, on separate line below on mobile)
 * - Automatic filter sheet pattern on mobile when multiple filters exist
 * - Automatic sizing: First child (SearchInput) max-w-[320px], filters min-w-[160px] max-w-[260px]
 * - Components with inline minWidth (like DateSelect: 260px for ranges) automatically respected
 * - For many filters: Set useExpandedFilters={true} for automatic overflow (uses default 160-260px sizing)
 *
 * Note: Action buttons should be placed in TableHeading.actions, not here.
 * TableControls is specifically for filters/search that appear in the table header row.
 *
 * **Row Selection Tables (Automatic Mobile Checkbox):**
 * For tables with bulk actions, provide `onToggleAllRows`, `allRowsSelected`, and `someRowsSelected` props.
 * TableControls will automatically generate a mobile select-all checkbox.
 * The component will warn you in development mode if these props are missing.
 *
 * **When to Show TableControls:**
 * - Show when there are records to search/filter (even if currently filtered to zero results)
 * - Hide when table is completely empty (no data at all) - users can't search/filter nothing
 * - Use renderControls conditionally: `renderControls={data.length > 0 ? () => <TableControls>... : undefined}`
 *
 * **Automatic Mobile Behavior:**
 * - 1 child (just search): Shown inline, full width
 * - 2+ children (search + filters): First child inline, rest in side sheet via filter icon
 * - afterSearch: Centered below search on compact layouts, to the right of search on desktop; never placed in the compact filter sheet
 *
 * **Desktop Behavior:**
 * - Standard: All children shown inline with automatic sizing (160-260px)
 * - With useExpandedFilters: Filters use standard sizing (160-260px), dynamically overflow into "Filters" button
 * - Pagination displays inline with filters
 *
 * @example
 * Standard mode: SearchInput (320px max) + Select filters (160-260px default)
 * DateSelect: Has built-in minWidth (260px for ranges, 160px single) - no wrapper needed
 * Many filters: Set useExpandedFilters={true} for automatic overflow management
 */
export function TableControls({
  pagination,
  children,
  activeFilters,
  onSortClick,
  useExpandedFilters = true,
  expandedFiltersState,
  onClearAllFilters,
  bulkActions = [],
  rowSelectionCount = 0,
  onShowSelectedOnly,
  isSelectedOnly = false,
  bulkSelection,
  onDeselectAll,
  totalRowCount,
  onToggleAllRows,
  allRowsSelected = false,
  someRowsSelected = false,
  filterChips = [],
  filterChipsAriaLabel,
  mutuallyExclusiveChips = false,
  onSelectAllAcrossPages,
  selectAllAcrossPagesLoading = false,
  totalMatchingCount,
  afterSearch
}: PropsWithChildren<TableControlsProps>) {
  const count = bulkSelection?.rowSelectionCount ?? rowSelectionCount;
  const onShowSelected = bulkSelection?.onShowSelectedOnly ?? onShowSelectedOnly;
  const selectedOnly = bulkSelection?.isSelectedOnly ?? isSelectedOnly;

  const processedFilterChips = useMemo(() => {
    if (!mutuallyExclusiveChips || filterChips.length === 0) {
      return filterChips;
    }

    return filterChips.map((chipConfig, index) => {
      const originalOnClick = chipConfig.onClick;
      if (!originalOnClick) {
        return chipConfig;
      }

      return {
        ...chipConfig,
        onClick: () => {
          if (chipConfig.selected) {
            originalOnClick();
            return;
          }

          originalOnClick();

          filterChips.forEach((otherChip, otherIndex) => {
            if (otherIndex !== index && otherChip.selected && otherChip.onClick) {
              otherChip.onClick();
            }
          });
        }
      };
    });
  }, [filterChips, mutuallyExclusiveChips]);

  const filterChipsRow =
    processedFilterChips.length > 0 ? (
      <div
        className="flex items-center gap-4 mt-3 flex-wrap"
        role={filterChipsAriaLabel ? "group" : undefined}
        aria-label={filterChipsAriaLabel}
      >
        {processedFilterChips.map((chipConfig, index) => (
          <Chip
            key={chipConfig.label ?? index}
            label={chipConfig.label}
            onClick={chipConfig.onClick}
            selected={chipConfig.selected}
            softDisabled={chipConfig.softDisabled}
          />
        ))}
      </div>
    ) : null;

  const childArray = Children.toArray(children);
  const hasMultipleChildren = childArray.length > 1;
  const hasRowSelection = count > 0;

  const mobileCheckbox =
    bulkActions.length > 0 && onToggleAllRows ? (
      <Checkbox
        name="select-all"
        checked={allRowsSelected}
        indeterminate={someRowsSelected}
        onChange={(checked) => {
          onToggleAllRows(checked);
        }}
      />
    ) : undefined;

  if (hasMultipleChildren) {
    const [firstChild, ...restChildren] = childArray;

    const filterItems = restChildren
      .filter((child: any) => child != null)
      .map((child: any) => {
        const childName = child?.props?.name;
        const hasCustomSizeAttribute = child?.props?.["data-filter-size"] === "custom";
        const hasInlineMinWidth = !!child?.props?.style?.minWidth;

        const hasCustomSizing = hasCustomSizeAttribute || hasInlineMinWidth;

        return {
          component: child,
          wrappedComponent: hasCustomSizing ? (
            child
          ) : (
            <div className="min-w-[160px] max-w-[200px]" data-name={childName}>
              {child}
            </div>
          ),
          name: childName
        };
      });

    return (
      <div>
        <div className="flex flex-col lg:flex-row lg:items-center gap-3">
          <div className="flex flex-col lg:flex-row lg:items-center gap-3 flex-1">
            {hasRowSelection ? (
              <>
                <div className="hidden lg:flex lg:items-center gap-3 flex-1">
                  <SearchWithAfterSearch search={firstChild} afterSearch={afterSearch} />
                  {bulkActions.map((action, index) =>
                    action.render ? (
                      <Fragment key={index}>{action.render()}</Fragment>
                    ) : (
                      <Button
                        key={index}
                        variant={action.variant || "outlined"}
                        icon={action.icon}
                        onClick={action.onClick}
                        disabled={action.disabled}
                        testId={action.testId}
                      >
                        {action.label}
                      </Button>
                    )
                  )}
                </div>

                <div className="flex lg:hidden flex-col gap-3 w-full">
                  <SearchWithAfterSearch search={firstChild} afterSearch={afterSearch} />
                  {bulkActions.length > 0 && (
                    <div className="flex gap-2 items-center flex-wrap">
                      {bulkActions[0].render ? (
                        bulkActions[0].render()
                      ) : (
                        <Button
                          variant={bulkActions[0].variant || "outlined"}
                          icon={bulkActions[0].icon}
                          onClick={bulkActions[0].onClick}
                          disabled={bulkActions[0].disabled}
                          testId={bulkActions[0].testId}
                        >
                          {bulkActions[0].label}
                        </Button>
                      )}

                      {bulkActions.length === 2 &&
                        (bulkActions[1].render ? (
                          bulkActions[1].render()
                        ) : (
                          <Button
                            variant={bulkActions[1].variant || "outlined"}
                            icon={bulkActions[1].icon}
                            onClick={bulkActions[1].onClick}
                            disabled={bulkActions[1].disabled}
                            testId={bulkActions[1].testId}
                          >
                            {bulkActions[1].label}
                          </Button>
                        ))}

                      {bulkActions.length > 2 && (
                        <Menu
                          AnchorComponent={<IconButton name="pending" data-testid="bulk-actions-menu-button" />}
                          menuItems={bulkActions.slice(1).map(
                            (action) =>
                              ({
                                label: action.label,
                                onClick: action.onClick,
                                ...(action.icon ? { iconName: action.icon } : {}),
                                dataTestId: action.testId
                              }) as MenuItem
                          )}
                        />
                      )}
                    </div>
                  )}
                </div>
              </>
            ) : (
              <>
                {useExpandedFilters && expandedFiltersState && onClearAllFilters ? (
                  <div className="hidden lg:flex lg:items-center gap-3 flex-1">
                    <SearchWithAfterSearch search={firstChild} afterSearch={afterSearch} />
                    <ExpandedFiltersWrapper
                      items={filterItems}
                      filters={expandedFiltersState}
                      onClearAll={onClearAllFilters}
                      activeFilters={activeFilters}
                    />
                  </div>
                ) : (
                  <div className="hidden lg:flex lg:items-center gap-3 flex-1">
                    <SearchWithAfterSearch search={firstChild} afterSearch={afterSearch} />
                    {restChildren.map((child, index) => (
                      <div key={index} className="min-w-[160px] max-w-[260px] flex-shrink-0">
                        {child}
                      </div>
                    ))}
                  </div>
                )}

                <div className="flex lg:hidden items-center gap-3 flex-1 min-w-0">
                  <SearchWithAfterSearch search={firstChild} afterSearch={afterSearch} />
                  {restChildren.length > 0 && (
                    <MenuFilters
                      content={
                        <div className="flex flex-col py-6">
                          {restChildren.map((child, index) => (
                            <div key={index} className={index < restChildren.length - 1 ? "mb-6" : ""}>
                              {child}
                            </div>
                          ))}
                          {useExpandedFilters && onClearAllFilters && !!activeFilters && activeFilters > 0 && (
                            <div className="mt-6">
                              <Button variant="text" onClick={onClearAllFilters}>
                                Reset All
                              </Button>
                            </div>
                          )}
                        </div>
                      }
                      activeFilters={activeFilters}
                    />
                  )}
                </div>
              </>
            )}
          </div>

          {!selectedOnly && pagination && <div className="hidden lg:flex items-center">{pagination}</div>}
        </div>

        {!hasRowSelection && filterChipsRow}

        {hasRowSelection && (onShowSelected || onDeselectAll || onSelectAllAcrossPages) && (
          <div className={classNames("flex items-center gap-4 mt-3", { "flex-wrap": onSelectAllAcrossPages })}>
            {onShowSelected && (
              <div className="flex items-center gap-2 md:mb-0">
                {onSelectAllAcrossPages && (
                  <span className="text-label-large">
                    {count} {pluralize(count, "row")} selected
                  </span>
                )}
                {onShowSelected && <Chip onClick={onShowSelected} label="Show Selected Only" selected={selectedOnly} />}
              </div>
            )}
            {onSelectAllAcrossPages && totalMatchingCount !== count && (
              <Button
                variant="text"
                onClick={onSelectAllAcrossPages}
                disabled={selectAllAcrossPagesLoading}
                testId="select-all-across-pages"
              >
                {selectAllAcrossPagesLoading ? "Selecting..." : `Select All ${totalMatchingCount} Rows`}
              </Button>
            )}
            {onDeselectAll && totalRowCount && (
              <Button variant="text" onClick={onDeselectAll}>
                Deselect All {totalRowCount} {pluralize(totalRowCount, "Row")}
              </Button>
            )}
          </div>
        )}

        {((!selectedOnly && pagination) || mobileCheckbox || onSortClick) && (
          <div
            className={`flex items-center lg:hidden ${!selectedOnly && pagination ? "mt-3" : "mt-4"} ${
              !mobileCheckbox && !onSortClick ? "justify-center" : "justify-between"
            }`}
          >
            {(mobileCheckbox || onSortClick) && (
              <div className="flex items-center gap-2">
                {mobileCheckbox}
                {onSortClick && (
                  <IconButton
                    name="sort_by_alpha"
                    onClick={onSortClick}
                    title="Sort Table"
                    data-testid="mobile-sort-button"
                    skipMargin
                    className="-ml-2"
                  />
                )}
              </div>
            )}
            {!selectedOnly && pagination}
          </div>
        )}
      </div>
    );
  }

  return (
    <div>
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
        <div className="flex flex-col lg:flex-row lg:items-center gap-3 flex-1">
          {hasRowSelection && bulkActions.length > 0 ? (
            <>
              <div className="hidden lg:flex lg:items-center gap-3 flex-1">
                <SearchWithAfterSearch search={childArray[0]} afterSearch={afterSearch} />
                {childArray.slice(1).map((child, index) => (
                  <div key={index} className="w-full lg:max-w-[320px]">
                    {child}
                  </div>
                ))}
                {bulkActions.map((action, index) =>
                  action.render ? (
                    <Fragment key={`action-${index}`}>{action.render()}</Fragment>
                  ) : (
                    <Button
                      key={`action-${index}`}
                      variant={action.variant || "outlined"}
                      icon={action.icon}
                      onClick={action.onClick}
                      disabled={action.disabled}
                      testId={action.testId}
                    >
                      {action.label}
                    </Button>
                  )
                )}
              </div>

              <div className="flex lg:hidden flex-col gap-3 w-full">
                <SearchWithAfterSearch search={childArray[0]} afterSearch={afterSearch} />
                {childArray.slice(1).map((child, index) => (
                  <div key={index} className="w-full">
                    {child}
                  </div>
                ))}
                <div className="flex gap-2 items-center flex-wrap">
                  {bulkActions[0].render ? (
                    bulkActions[0].render()
                  ) : (
                    <Button
                      variant={bulkActions[0].variant || "outlined"}
                      icon={bulkActions[0].icon}
                      onClick={bulkActions[0].onClick}
                      disabled={bulkActions[0].disabled}
                      testId={bulkActions[0].testId}
                    >
                      {bulkActions[0].label}
                    </Button>
                  )}

                  {bulkActions.length === 2 &&
                    (bulkActions[1].render ? (
                      bulkActions[1].render()
                    ) : (
                      <Button
                        variant={bulkActions[1].variant || "outlined"}
                        icon={bulkActions[1].icon}
                        onClick={bulkActions[1].onClick}
                        disabled={bulkActions[1].disabled}
                        testId={bulkActions[1].testId}
                      >
                        {bulkActions[1].label}
                      </Button>
                    ))}

                  {bulkActions.length > 2 && (
                    <Menu
                      AnchorComponent={<IconButton name="pending" data-testid="bulk-actions-menu-button" />}
                      menuItems={bulkActions.slice(1).map(
                        (action) =>
                          ({
                            label: action.label,
                            onClick: action.onClick,
                            ...(action.icon ? { iconName: action.icon } : {}),
                            dataTestId: action.testId
                          }) as MenuItem
                      )}
                    />
                  )}
                </div>
              </div>
            </>
          ) : (
            <>
              <SearchWithAfterSearch search={childArray[0]} afterSearch={afterSearch} />
              {childArray.slice(1).map((child, index) => (
                <div key={index} className="min-w-[140px] max-w-[200px]">
                  {child}
                </div>
              ))}
            </>
          )}
        </div>

        {!selectedOnly && pagination && <div className="hidden lg:flex items-center">{pagination}</div>}
      </div>

      {!hasRowSelection && filterChipsRow}

      {hasRowSelection && (onShowSelected || onDeselectAll || onSelectAllAcrossPages) && (
        <div className={classNames("flex items-center gap-4 mt-3", { "flex-wrap": onSelectAllAcrossPages })}>
          {onShowSelected && (
            <div className="flex items-center gap-2 md:mb-0">
              {onSelectAllAcrossPages && (
                <span className="text-label-large">
                  {count} {pluralize(count, "row")} selected
                </span>
              )}
              {onShowSelected && <Chip onClick={onShowSelected} label="Show Selected Only" selected={selectedOnly} />}
            </div>
          )}
          {onSelectAllAcrossPages && totalMatchingCount !== count && (
            <Button
              variant="text"
              onClick={onSelectAllAcrossPages}
              disabled={selectAllAcrossPagesLoading}
              testId="select-all-across-pages"
            >
              {selectAllAcrossPagesLoading ? "Selecting..." : `Select All ${totalMatchingCount} Rows`}
            </Button>
          )}
          {onDeselectAll && totalRowCount && (
            <Button variant="text" onClick={onDeselectAll}>
              Deselect All {totalRowCount} {pluralize(totalRowCount, "Row")}
            </Button>
          )}
        </div>
      )}

      {((!selectedOnly && pagination) || mobileCheckbox || onSortClick) && (
        <div
          className={`flex items-center lg:hidden ${!selectedOnly && pagination ? "mt-3" : "mt-4"} ${
            !mobileCheckbox && !onSortClick ? "justify-center" : "justify-between"
          }`}
        >
          {(mobileCheckbox || onSortClick) && (
            <div className="flex items-center gap-2">
              {mobileCheckbox}
              {onSortClick && (
                <IconButton
                  name="sort_by_alpha"
                  onClick={onSortClick}
                  title="Sort Table"
                  data-testid="mobile-sort-button"
                  skipMargin
                  className="-ml-2"
                />
              )}
            </div>
          )}
          {!selectedOnly && pagination}
        </div>
      )}
    </div>
  );
}
